//@ts-nocheck
import React from "react";
import { motion } from "framer-motion";
import { Copy } from "lucide-react";
import {
  INDONESIA_TALK_COLORS,
  INDONESIA_TALK_LAYOUT,
  INDONESIA_TALK_ANIMATION,
  INDONESIA_TALK_DEPTH_COLOR,
  INDONESIA_TALK_HEADER_COPY,
  IndonesiaTalkDepthLevel,
} from "./astriaIndonesiaTalkTheme";

// ─────────────────────────────────────────────────────────────────────────────
// ASTRIA INDONESIA TALK — "Teman Bicara" card renderer
// Renders the AI's streamed text response for the "Astria Indonesia Talk"
// category's conversational modes (Daily Atmosphere / Companion / Love &
// Family / Coach / Culture / Primbon Light — see
// helper/AstriaIndonesiaTalkService.js on the backend) inside
// the soft/warm/reflective card described in the client's "Indo Emotional
// UI Pack v1" (Ui.txt): persona tag, emotional header, depth cue, chat
// bubble, input box tokens — see astriaIndonesiaTalkTheme.ts.
//
// Modeled on AstriaJapanTalkCard.tsx's structure, but — unlike Japan Talk's
// card, which is currently switched off in ChatInterface.tsx by product
// decision — this one ships enabled by default.
//
// Scope: "Astria Indonesia Talk" ONLY. Does not touch "Astria Indonesia"
// (v1) or "Astria Indonesia V2" (see indonesiaV2/) — purely additive
// renderer swapped in by ChatInterface.tsx only when category name ===
// "Astria Indonesia Talk".
// ─────────────────────────────────────────────────────────────────────────────

type IndonesiaTalkMode =
  | "daily_atmosphere"
  | "companion"
  | "love_family"
  | "coach"
  | "culture"
  | "primbon";

const MODE_META: Record<
  IndonesiaTalkMode,
  { title: string; personaTag: string; emoji: string }
> = {
  daily_atmosphere: {
    title: "Suasana Harian",
    personaTag: "Daily Atmosphere",
    emoji: "🌤️",
  },
  companion: { title: "Teman Bicara", personaTag: "Companion", emoji: "🌙" },
  love_family: {
    title: "Cinta & Keluarga",
    personaTag: "Love & Family",
    emoji: "💗",
  },
  coach: { title: "Panduan Hidup", personaTag: "Coach", emoji: "🧭" },
  culture: { title: "Budaya & Hari Ini", personaTag: "Culture", emoji: "🎈" },
  primbon: {
    title: "Primbon Ringan",
    personaTag: "Primbon Light",
    emoji: "🍃",
  },
};

// Mirrors the backend's ID_TALK_MODE_MAP in helper/AstriaIndonesiaTalkService.js
// exactly — same order, same keywords — so the persona tag shown here always
// matches the mode the backend actually used to build the reply.
function resolveIndonesiaTalkMode(
  subCategoryName?: string | null,
): IndonesiaTalkMode {
  if (!subCategoryName) return "companion";
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("primbon")) return "primbon";
  if (
    lower.includes("mu") ||
    lower.includes("culture") ||
    lower.includes("budaya")
  )
    return "culture";
  if (
    lower.includes("coach") ||
    lower.includes("life direction") ||
    lower.includes("work")
  )
    return "coach";
  if (
    lower.includes("love & family") ||
    lower.includes("love and family") ||
    lower.includes("love") ||
    lower.includes("family") ||
    lower.includes("pasangan") ||
    lower.includes("hubungan") ||
    lower.includes("toxic") ||
    lower.includes("patah hati")
  )
    return "love_family";
  if (
    lower.includes("daily atmosphere") ||
    lower.includes("atmosphere") ||
    lower.includes("cuaca")
  )
    return "daily_atmosphere";
  return "companion";
}

// Best-effort depth inference from the AI's own text — the backend already
// decides depth server-side (AstriaIndonesiaTalkService.js's
// intensityEffects()), but that value doesn't currently ride along in the
// chat message payload, so this is a lightweight visual-only proxy until a
// structured field is added (same idea as astriaKoreaV2Data — see
// ChatContext.tsx's Message interface). Longer replies read as "going
// deeper".
function inferDepthLevel(content: string): IndonesiaTalkDepthLevel {
  const len = (content || "").trim().length;
  if (len > 900) return "very_deep";
  if (len > 500) return "deep";
  if (len > 200) return "medium";
  return "shallow";
}

// Mirrors inferDepthLevel's proxy approach for the low/medium/high buckets
// the emotional header needs — until the backend rides its own
// detectEmotionalIntensity() result along on the message, per this same
// length heuristic collapsed to 3 buckets instead of 4.
function inferHeaderIntensity(content: string): "low" | "medium" | "high" {
  const len = (content || "").trim().length;
  if (len > 700) return "high";
  if (len > 300) return "medium";
  return "low";
}

interface AstriaIndonesiaTalkCardProps {
  content: string;
  subCategoryName?: string | null;
  isStreaming?: boolean;
  timestamp?: string | number | Date;
  onCopy?: () => void;
}

const AstriaIndonesiaTalkCard: React.FC<AstriaIndonesiaTalkCardProps> = ({
  content,
  subCategoryName,
  isStreaming,
  timestamp,
  onCopy,
}) => {
  const mode = resolveIndonesiaTalkMode(subCategoryName);
  const meta = MODE_META[mode];
  const body = content || "";
  const depth = inferDepthLevel(body);
  const headerIntensity = inferHeaderIntensity(body);
  const headerCopy = INDONESIA_TALK_HEADER_COPY[headerIntensity];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={INDONESIA_TALK_ANIMATION.fadeSoft}
      style={{
        background: INDONESIA_TALK_COLORS.bgSecondary,
        borderRadius: INDONESIA_TALK_LAYOUT.cardRadius,
        boxShadow: INDONESIA_TALK_COLORS.shadowCard,
        maxWidth: 720,
        width: "100%",
        margin: "0 auto",
        padding: INDONESIA_TALK_LAYOUT.globalPadding,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Emotional header — "the most important" component per Ui.txt */}
      <div
        style={{
          marginBottom: 16,
          borderRadius: INDONESIA_TALK_LAYOUT.cardRadius,
          padding: "12px 16px",
          background: `linear-gradient(135deg, ${INDONESIA_TALK_COLORS.headerGradient[0]}, ${INDONESIA_TALK_COLORS.headerGradient[1]})`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>{meta.emoji}</span>
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: INDONESIA_TALK_COLORS.textSecondary,
                lineHeight: 1.3,
              }}
            >
              {meta.title}
            </span>
          </div>

          {/* Depth cue — right side of message bubble, per Ui.txt spec */}
          <span
            title={depth.replace("_", " ")}
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: INDONESIA_TALK_DEPTH_COLOR[depth],
              flexShrink: 0,
            }}
          />
        </div>

        <div
          style={{
            marginTop: 6,
            fontSize: 13,
            fontWeight: 400,
            color: INDONESIA_TALK_COLORS.textMuted,
            lineHeight: 1.4,
          }}
        >
          {headerCopy.id}
        </div>
      </div>

      {/* Persona tag — bottom-left of the message, per Ui.txt spec */}
      <div style={{ marginBottom: 10 }}>
        <span
          style={{
            display: "inline-block",
            background: INDONESIA_TALK_COLORS.personaTagBg,
            color: INDONESIA_TALK_COLORS.personaTagText,
            borderRadius: INDONESIA_TALK_LAYOUT.personaTagRadius,
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {meta.personaTag}
        </span>
      </div>

      {/* Chat bubble */}
      <div
        style={{
          background: INDONESIA_TALK_COLORS.chatBubbleAiBg,
          borderRadius: INDONESIA_TALK_LAYOUT.bubbleRadius,
          padding: 20,
          fontSize: 15,
          lineHeight: INDONESIA_TALK_LAYOUT.lineHeight,
          color: INDONESIA_TALK_COLORS.textPrimary,
          whiteSpace: "pre-wrap",
        }}
      >
        {body}
      </div>

      {isStreaming && (
        <div style={{ display: "flex", gap: 4, marginTop: 16, paddingLeft: 4 }}>
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: INDONESIA_TALK_COLORS.accentIndigo,
                display: "inline-block",
              }}
            />
          ))}
        </div>
      )}

      {/* Timestamp + copy — rendered inside the card, not by the outer chat bubble */}
      {(timestamp || onCopy) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 14,
            paddingTop: 12,
            borderTop: `1px solid ${INDONESIA_TALK_COLORS.personaTagBg}`,
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: INDONESIA_TALK_COLORS.textMuted,
            }}
          >
            {timestamp
              ? new Date(timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""}
          </span>
          {onCopy && (
            <button
              type="button"
              onClick={onCopy}
              title="Copy"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 6,
                borderRadius: 8,
                background: "transparent",
                border: "none",
                color: INDONESIA_TALK_COLORS.textMuted,
                cursor: "pointer",
                transition: "background 0.2s ease, color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = INDONESIA_TALK_COLORS.personaTagBg;
                el.style.color = INDONESIA_TALK_COLORS.accentIndigo;
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = "transparent";
                el.style.color = INDONESIA_TALK_COLORS.textMuted;
              }}
            >
              <Copy size={13} />
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default AstriaIndonesiaTalkCard;
